import { encryptGroupMessage, encryptMessage } from "@/utils/crypto";
import { getActiveKeySet, getPublicKeySetByUsername, getSymmetricKey } from "@/services/keyStorageService";
import { normalizeGroupAddress } from "@/services/groupKeyService";

/** A 1:1 ciphertext plus the version of the peer key it was made for. */
export interface PeerCiphertext {
  /** ECDH wire string: Base64(IV):Base64(ciphertext):Base64(signature) */
  wire: string;
  /**
   * The peer's `timestamp_generated` as the router stored it — sent as `e2e_keys_version` so the
   * router rejects a send made for a superseded key. 0 when unknown, which the router doesn't check.
   */
  keysVersion: number;
}

/**
 * Encrypts a 1:1 message for `recipientUsername` from IndexedDB only — no @keys fetch per send.
 * Shared by the composer (useEncryption) and resendMessage, which has no hook to call.
 */
export async function encryptForPeer(message: string, recipientUsername: string, userId: number): Promise<PeerCiphertext> {
  const [peerKeys, ownKeySet] = await Promise.all([
    getPublicKeySetByUsername(recipientUsername),
    getActiveKeySet(userId),
  ]);
  if (!peerKeys) {
    throw new Error(`No public key for user ${recipientUsername}. They need to share their key first.`);
  }
  if (!ownKeySet) throw new Error("No active key set found.");

  const wire = await encryptMessage(
    message,
    peerKeys.encryptPublicKeyJwk,
    ownKeySet.encryptPrivateKeyJwk,
    ownKeySet.signPrivateKeyJwk
  );
  // Number(): the router may relay timestamp_generated as a string.
  return { wire, keysVersion: Number(peerKeys.timestampGenerated) || 0 };
}

/**
 * Encrypts a group message with the group's symmetric key, signed by the sender (3-part wire).
 * Refuses a stale key (owner rotated) — members couldn't read it; only owners' entries never go stale.
 */
export async function encryptForGroupAddress(message: string, groupAddress: string, userId: number): Promise<string> {
  const entry = await getSymmetricKey(normalizeGroupAddress(groupAddress));
  if (!entry || entry.stale) throw new Error("No current symmetric key for this group.");

  const ownKeySet = await getActiveKeySet(userId);
  if (!ownKeySet) throw new Error("No active key set found.");

  return encryptGroupMessage(message, entry.symmetricKeyB64, ownKeySet.signPrivateKeyJwk);
}
